/**
 * Problems: what makes a graphic fail with real data, computed from the canvas renders (with the test values and
 * with the field defaults), the fonts' character maps and the fields. Shown live on the canvas and listed in the
 * Problems panel. Messages are i18n keys with parameters.
 */
import { customSizeProblem, timelinePhases, type Element, type Scene } from '@prism-graphics/scene-schema';
import type { Rect } from './commands.ts';
import { validateValue } from './fields.ts';
import { safeArea } from './snapping.ts';
import { walk } from './tree.ts';

export type Severity = 'error' | 'warning' | 'info';
export type DataSource = 'test' | 'default' | 'both';

export interface Problem {
  key: string;
  severity: Severity;
  code: string;
  params: Record<string, string | number>;
  elementId?: string;
  fieldId?: string;
  source?: DataSource;
}

export interface FrameReport {
  text: Array<{ id: string; problem?: 'overflow' | 'truncated' | 'clamped'; fontSize: number }>;
  geometry: ReadonlyMap<string, { bounds: Rect; visible: boolean }>;
  data: Record<string, unknown>;
}

export interface FontInfo {
  family?: string;
  codePoints?: Array<[number, number]>;
  coverage?: Record<string, { missing: string[] }>;
}

export interface ProblemInput {
  scene: Scene;
  test?: FrameReport | undefined;
  /** The render with the field defaults (omitted when the test values are the defaults). */
  defaults?: FrameReport | undefined;
  /** Font info by asset hash (from the library); fonts missing here are not checked. */
  fonts: ReadonlyMap<string, FontInfo>;
  /** Library hashes, to flag scene assets the library lacks (undefined: not loaded yet). */
  library?: ReadonlySet<string> | undefined;
  languages: readonly string[];
  /** Graphics-safe margin in percent (default 5). */
  safeMargin?: number;
  testData?: Record<string, unknown>;
  /** Library facts about assets (size, MIME type, video alpha), for media and package size warnings. */
  media?: ReadonlyMap<string, MediaInfo>;
  /** Package size warning limit (bytes, from the API configuration). */
  packageLimit?: number;
}

export interface MediaInfo {
  size: number;
  mime: string;
  alpha?: boolean;
}

/** A video larger than this gets a warning (it is in every package and loads before the first frame). */
export const LARGE_VIDEO_BYTES = 25 * 1024 * 1024;
/** What a package holds besides its assets: graphic.mjs (runtime + scene), scene.json, manifest, thumbnail. */
const PACKAGE_OVERHEAD = 120 * 1024;

const SAFE_TOLERANCE = 0.5;

export function covers(ranges: ReadonlyArray<[number, number]>, cp: number): boolean {
  let lo = 0;
  let hi = ranges.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const [a, b] = ranges[mid]!;
    if (cp < a) hi = mid - 1;
    else if (cp > b) lo = mid + 1;
    else return true;
  }
  return false;
}

/** Characters of `text` the font cannot show (whitespace and control characters ignored). */
export function missingGlyphs(text: string, ranges: ReadonlyArray<[number, number]>): string[] {
  const out = new Set<string>();
  for (const ch of text) {
    const cp = ch.codePointAt(0)!;
    if (cp < 0x21 || (cp >= 0x7f && cp < 0xa0) || /\s/.test(ch)) continue;
    if (!covers(ranges, cp)) out.add(ch);
  }
  return [...out];
}

function merge(a: Problem[], b: Problem[]): Problem[] {
  const out = new Map<string, Problem>();
  for (const p of a) out.set(p.key, { ...p, source: 'test' });
  for (const p of b) {
    const prev = out.get(p.key);
    out.set(p.key, prev ? { ...prev, source: 'both' } : { ...p, source: 'default' });
  }
  return [...out.values()];
}

const SAFE_TYPES = new Set<Element['type']>(['text', 'image', 'video', 'rect', 'ellipse', 'line']);

function frameProblems(scene: Scene, frame: FrameReport, margin: number, names: Map<string, Element>): Problem[] {
  const out: Problem[] = [];
  for (const t of frame.text) {
    if (!t.problem) continue;
    const el = names.get(t.id);
    out.push({ key: `text:${t.id}`, severity: 'warning', code: `problem.text.${t.problem}`, params: { name: el?.name || t.id }, elementId: t.id });
  }
  const safe = safeArea(scene.canvas.width, scene.canvas.height, margin);
  const canvasArea = scene.canvas.width * scene.canvas.height;
  for (const [id, g] of frame.geometry) {
    const el = names.get(id);
    if (!el || !g.visible || !el.visible) continue;
    const hasFill = (el.type === 'group' && el.style.fill !== undefined) || SAFE_TYPES.has(el.type);
    if (!hasFill) continue;
    const b = g.bounds;
    if (b.width <= 0 || b.height <= 0) continue;
    // Full-frame backgrounds are meant to cover the canvas.
    if (b.width * b.height >= 0.9 * canvasArea) continue;
    const outside =
      b.x < safe.x - SAFE_TOLERANCE || b.y < safe.y - SAFE_TOLERANCE || b.x + b.width > safe.x + safe.width + SAFE_TOLERANCE || b.y + b.height > safe.y + safe.height + SAFE_TOLERANCE;
    if (outside) out.push({ key: `safe:${id}`, severity: 'warning', code: 'problem.safeArea', params: { name: el.name || id, margin }, elementId: id });
  }
  return out;
}

export function computeProblems(input: ProblemInput): Problem[] {
  const { scene } = input;
  const margin = input.safeMargin ?? 5;
  const els = new Map<string, Element>();
  walk(scene.elements, (el) => els.set(el.id, el));
  const problems: Problem[] = [];

  problems.push(...merge(input.test ? frameProblems(scene, input.test, margin, els) : [], input.defaults ? frameProblems(scene, input.defaults, margin, els) : []));

  // Glyphs: what each text element shows (test value, default) against its font's character map.
  const shown = (frame: FrameReport | undefined, el: Element & { type: 'text' }): string => {
    const f = scene.fields.find((x) => x.bindings.some((b) => b.element === el.id && b.property === 'text'));
    if (f && frame && typeof frame.data[f.id] === 'string') return frame.data[f.id] as string;
    if (f && f.type === 'string') return f.default;
    return el.text;
  };
  for (const el of els.values()) {
    if (el.type !== 'text') continue;
    const font = input.fonts.get(el.style.font);
    if (!font?.codePoints) continue;
    const text = `${shown(input.test, el)}${shown(input.defaults, el)}${el.text}`;
    const missing = missingGlyphs(text, font.codePoints);
    if (missing.length) {
      problems.push({ key: `glyphs:${el.id}`, severity: 'warning', code: 'problem.glyphs', params: { name: el.name || el.id, chars: missing.join(' '), font: font.family ?? '' }, elementId: el.id });
    }
  }

  // Font coverage for the configured languages, once per font used.
  const usedFonts = new Set<string>();
  for (const el of els.values()) if (el.type === 'text') usedFonts.add(el.style.font);
  for (const hash of usedFonts) {
    const font = input.fonts.get(hash);
    if (!font?.coverage) continue;
    for (const lang of input.languages) {
      const missing = font.coverage[lang]?.missing ?? [];
      if (missing.length) problems.push({ key: `coverage:${hash}:${lang}`, severity: 'warning', code: 'problem.coverage', params: { font: font.family ?? hash.slice(7, 15), lang, chars: missing.slice(0, 12).join(' ') } });
    }
  }

  // Effects that misbehave over alpha (Broadcast quality requirements): blend modes mix with the graphic's own
  // transparent pixels, never with the video it is keyed over; blur over large areas costs real-time performance.
  const blurred = new Set<string>();
  for (const [, phase] of timelinePhases(scene.timeline)) for (const tr of phase.tracks) if (tr.property === 'blur') blurred.add(tr.element);
  const canvasArea = scene.canvas.width * scene.canvas.height;
  const frame = input.test ?? input.defaults;
  for (const el of els.values()) {
    if (el.blendMode && el.blendMode !== 'normal') {
      problems.push({ key: `blend:${el.id}`, severity: 'warning', code: 'problem.blendMode', params: { name: el.name || el.id, mode: el.blendMode }, elementId: el.id });
    }
    const styleBlur = (el.type === 'rect' || el.type === 'ellipse' || el.type === 'group') && (el.style.blur ?? 0) > 0;
    if (styleBlur || blurred.has(el.id)) {
      const b = frame?.geometry.get(el.id)?.bounds;
      if (b && b.width * b.height > 0.25 * canvasArea) problems.push({ key: `blur:${el.id}`, severity: 'warning', code: 'problem.blurLarge', params: { name: el.name || el.id }, elementId: el.id });
    }
    if (el.type === 'video') {
      const m: MediaInfo | undefined = input.media?.get(el.asset) ?? (() => {
        const a = scene.assets.find((x) => x.hash === el.asset);
        return a ? { size: 0, mime: a.mime } : undefined;
      })();
      if (m?.mime === 'video/mp4') problems.push({ key: `video:mp4:${el.id}`, severity: 'warning', code: 'problem.video.mp4', params: { name: el.name || el.id }, elementId: el.id });
      else if (m && m.alpha === false) problems.push({ key: `video:opaque:${el.id}`, severity: 'info', code: 'problem.video.opaque', params: { name: el.name || el.id }, elementId: el.id });
      if (m && m.size > LARGE_VIDEO_BYTES) problems.push({ key: `video:large:${el.id}`, severity: 'warning', code: 'problem.video.large', params: { name: el.name || el.id, size: Math.round(m.size / 1024 / 1024) }, elementId: el.id });
    }
  }

  // Package size (an estimate from the library's asset sizes; the export reports the exact size).
  if (input.packageLimit && input.media) {
    const bytes = PACKAGE_OVERHEAD + scene.assets.reduce((n, a) => n + (input.media!.get(a.hash)?.size ?? 0), 0);
    if (bytes > input.packageLimit) problems.push({ key: 'package:size', severity: 'warning', code: 'problem.packageLarge', params: { size: Math.round(bytes / 1024 / 102.4) / 10, limit: Math.round(input.packageLimit / 1024 / 102.4) / 10 } });
  }

  // A canvas size PRISM's custom sequence format would not take (odd, or outside 16–7680 × 16–4320).
  const size = customSizeProblem(scene.canvas);
  if (size) problems.push({ key: 'canvas:size', severity: 'warning', code: `problem.canvas.${size}`, params: { width: scene.canvas.width, height: scene.canvas.height } });

  // Fields.
  for (const f of scene.fields) {
    if (f.bindings.length === 0) problems.push({ key: `field:unbound:${f.id}`, severity: 'info', code: 'problem.field.unbound', params: { title: f.title }, fieldId: f.id });
    const v = input.testData?.[f.id];
    if (v !== undefined) {
      const err = validateValue(f, v);
      if (err) problems.push({ key: `field:test:${f.id}`, severity: 'info', code: 'problem.field.testValue', params: { title: f.title, error: err }, fieldId: f.id });
    }
  }

  // Assets the library does not have (cannot be exported).
  if (input.library) {
    for (const a of scene.assets) {
      if (!input.library.has(a.hash)) problems.push({ key: `asset:${a.hash}`, severity: 'error', code: 'problem.assetMissing', params: { file: a.fileName } });
    }
  }

  const rank: Record<Severity, number> = { error: 0, warning: 1, info: 2 };
  return problems.sort((a, b) => rank[a.severity] - rank[b.severity]);
}
